import React from 'react';
import { useCurrency } from '../../../hooks/useCurrency';
import type { TrialBalanceData, TrialBalanceItem } from '../../../types/reports';
import { CurrencyCode } from '../../../config/currency';

interface TrialBalanceProps {
  data: TrialBalanceData;
  currency?: CurrencyCode;
}

/** Libellé de classe SYSCOHADA */
const CLASS_LABELS: Record<string, string> = {
  '1': 'Classe 1 — Capitaux',
  '2': 'Classe 2 — Actif immobilisé',
  '3': 'Classe 3 — Stocks',
  '4': 'Classe 4 — Tiers',
  '5': 'Classe 5 — Trésorerie',
  '6': 'Classe 6 — Charges',
  '7': 'Classe 7 — Produits',
  '8': 'Classe 8 — Résultats',
};


const renderAmount = (n: number, fmt: (v: number) => string) => {
  if (n === 0) return <span className="text-gray-300">—</span>;
  return (
    <span className={n < 0 ? 'text-red-600' : undefined}>
      {fmt(n)}
    </span>
  );
};

function ItemRow({
  item,
  has6Cols,
  fmt,
}: {
  item: TrialBalanceItem;
  has6Cols: boolean;
  fmt: (n: number) => string;
}) {
  const { account, opening, movements, balance } = item;
  return (
    <tr className="hover:bg-gray-50 even:bg-white odd:bg-gray-50/30">
      <td className="px-3 py-1.5 text-xs font-mono text-gray-700 whitespace-nowrap">{account.code}</td>
      <td className="px-3 py-1.5 text-xs text-gray-800 max-w-xs truncate" title={account.name}>{account.name}</td>

      {/* Colonnes Ouverture — uniquement en 6 colonnes */}
      {has6Cols && (
        <>
          <td className="px-3 py-1.5 text-xs text-right">{renderAmount(opening?.debit ?? 0, fmt)}</td>
          <td className="px-3 py-1.5 text-xs text-right">{renderAmount(opening?.credit ?? 0, fmt)}</td>
        </>
      )}

      {/* Mouvements */}
      <td className="px-3 py-1.5 text-xs text-right">{renderAmount(movements.debit, fmt)}</td>
      <td className="px-3 py-1.5 text-xs text-right">{renderAmount(movements.credit, fmt)}</td>

      {/* Soldes */}
      <td className="px-3 py-1.5 text-xs text-right font-medium">{renderAmount(balance.debitBalance, fmt)}</td>
      <td className="px-3 py-1.5 text-xs text-right font-medium">{renderAmount(balance.creditBalance, fmt)}</td>
    </tr>
  );
}

function TotalsRow({
  totals,
  has6Cols,
  fmt,
}: {
  totals: TrialBalanceData['totals'];
  has6Cols: boolean;
  fmt: (n: number) => string;
}) {
  return (
    <tr className="bg-gray-100 font-semibold border-t-2 border-gray-400">
      <td className="px-3 py-2 text-xs" colSpan={2}>TOTAUX GÉNÉRAUX</td>
      {has6Cols && (
        <>
          <td className="px-3 py-2 text-xs text-right">{fmt(totals.totalOpeningDebit)}</td>
          <td className="px-3 py-2 text-xs text-right">{fmt(totals.totalOpeningCredit)}</td>
        </>
      )}
      <td className="px-3 py-2 text-xs text-right">{fmt(totals.totalDebit)}</td>
      <td className="px-3 py-2 text-xs text-right">{fmt(totals.totalCredit)}</td>
      <td className="px-3 py-2 text-xs text-right">{fmt(totals.totalDebitBalance)}</td>
      <td className="px-3 py-2 text-xs text-right">{fmt(totals.totalCreditBalance)}</td>
    </tr>
  );
}

export function TrialBalance({ data, currency }: TrialBalanceProps) {
  const { formatConverted } = useCurrency();
  const fmt = (n: number) => formatConverted(n, currency);

  const has6Cols = data.format === '6-columns-syscohada';
  const hasByClass = data.byClass && Object.keys(data.byClass).length > 0;

  // Choisir entre affichage par classe ou liste plate
  const items = data.items ?? [];

  return (
    <div className="space-y-4">
      {/* En-tête info */}
      <div className="flex flex-wrap gap-4 text-sm text-gray-600">
        <span>
          <strong>Format :</strong>{' '}
          {has6Cols ? '6 colonnes SYSCOHADA (avec à-nouveaux)' : '4 colonnes'}
        </span>
        <span>
          <strong>Comptes :</strong> {data.accountCount}
        </span>
        <span>
          <strong>Date :</strong>{' '}
          {data.period.asOfDate ? new Date(data.period.asOfDate).toLocaleDateString('fr-CD') : '—'}
        </span>
        {has6Cols && data.period.openingDate && (
          <span>
            <strong>À-nouveaux :</strong>{' '}
            {new Date(data.period.openingDate).toLocaleDateString('fr-CD')}
          </span>
        )}
      </div>

      {/* Indicateur d'équilibre */}
      <div className="flex gap-3 text-sm">
        <span
          className={`px-3 py-1 rounded-full font-medium ${
            data.controls.isBalanced ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          }`}
        >
          {data.controls.isBalanced ? '✓ Balance équilibrée' : '✗ Balance déséquilibrée'}
        </span>
        {data.controls.difference !== 0 && (
          <span className="px-3 py-1 rounded-full bg-orange-100 text-orange-700 font-medium">
            Écart : {fmt(data.controls.difference)}
          </span>
        )}
      </div>

      {/* Tableau */}
      <div className="border rounded-lg overflow-hidden overflow-x-auto">
        <table className="min-w-full text-xs divide-y divide-gray-200">
          <thead>
            <tr className="bg-gray-100">
              <th className="px-3 py-2 text-left font-semibold text-gray-600 uppercase tracking-wider" rowSpan={2}>
                Code
              </th>
              <th className="px-3 py-2 text-left font-semibold text-gray-600 uppercase tracking-wider" rowSpan={2}>
                Intitulé
              </th>
              {has6Cols && (
                <th
                  className="px-3 py-2 text-center font-semibold text-gray-600 uppercase tracking-wider border-l"
                  colSpan={2}
                >
                  Ouverture (à-nouveaux)
                </th>
              )}
              <th
                className="px-3 py-2 text-center font-semibold text-gray-600 uppercase tracking-wider border-l"
                colSpan={2}
              >
                Mouvements
              </th>
              <th
                className="px-3 py-2 text-center font-semibold text-gray-600 uppercase tracking-wider border-l"
                colSpan={2}
              >
                Soldes
              </th>
            </tr>
            <tr className="bg-gray-50">
              {has6Cols && (
                <>
                  <th className="px-3 py-1.5 text-right font-medium text-gray-500 border-l">Débit</th>
                  <th className="px-3 py-1.5 text-right font-medium text-gray-500">Crédit</th>
                </>
              )}
              <th className="px-3 py-1.5 text-right font-medium text-gray-500 border-l">Débit</th>
              <th className="px-3 py-1.5 text-right font-medium text-gray-500">Crédit</th>
              <th className="px-3 py-1.5 text-right font-medium text-gray-500 border-l">Débiteur</th>
              <th className="px-3 py-1.5 text-right font-medium text-gray-500">Créditeur</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {hasByClass
              ? Object.keys(data.byClass)
                  .sort((a, b) => Number(a) - Number(b))
                  .map((classKey) => (
                    <React.Fragment key={classKey}>
                      <tr className="bg-blue-50">
                        <td
                          colSpan={has6Cols ? 8 : 6}
                          className="px-3 py-2 text-xs font-bold text-blue-800 uppercase tracking-wide"
                        >
                          {CLASS_LABELS[classKey] ?? `Classe ${classKey}`}
                        </td>
                      </tr>
                      {data.byClass[classKey].map((item) => (
                        <ItemRow key={item.account.id} item={item} has6Cols={has6Cols} fmt={fmt} />
                      ))}
                    </React.Fragment>
                  ))
              : items.map((item) => (
                  <ItemRow key={item.account.id} item={item} has6Cols={has6Cols} fmt={fmt} />
                ))}

            <TotalsRow totals={data.totals} has6Cols={has6Cols} fmt={fmt} />
          </tbody>
        </table>
      </div>
    </div>
  );
}
